/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { FailureClassification, SessionEndReason } from "../enums";

interface TEndReasonWireCodes {
  endReasonCode: string;
  reason: string;
}

/**
 * Maps the internal end reason to the wire `er`/`rsn` codes. For error/policy
 * reasons the underlying error/policy code is used verbatim, matching
 * Android's behavior of emitting the error/policy code as both fields.
 */
export const mapEndReasonToWireCodes = (
  reason: SessionEndReason,
  underlyingCode?: string
): TEndReasonWireCodes => {
  switch (reason) {
    case SessionEndReason.USER_ENDED:
      return { endReasonCode: "USER_ENDED", reason: "ENDED_BY_USER" };
    case SessionEndReason.SWITCH_CHANNEL_REQUESTED:
      return {
        endReasonCode: "SWITCH_CHANNEL_REQUESTED",
        reason: "SWITCH_CHANNEL_REQUESTED"
      };
    case SessionEndReason.NETWORK_LOST:
      return { endReasonCode: "NETWORK_LOST", reason: "LOST_NETWORK" };
    case SessionEndReason.END_OF_CONTENT:
      return { endReasonCode: "END_OF_CONTENT", reason: "END_OF_CONTENT" };
    case SessionEndReason.SUPERSEDED:
      return { endReasonCode: "SUPERSEDED", reason: "SUPERSEDED" };
    case SessionEndReason.PLAYER_ERROR: {
      const code =
        underlyingCode !== undefined ? underlyingCode : "PLAYER_ERROR";
      return { endReasonCode: code, reason: code };
    }
    case SessionEndReason.POLICY_BLOCK: {
      const code =
        underlyingCode !== undefined ? underlyingCode : "POLICY_BLOCK";
      return { endReasonCode: code, reason: code };
    }
    default:
      return { endReasonCode: "USER_ENDED", reason: "ENDED_BY_USER" };
  }
};

/**
 * Derives `failure_classification` from the end reason and whether any
 * playback occurred during the session. Mirrors spec §7.2 exactly.
 */
export const classifyFailure = (
  reason: SessionEndReason,
  hasPlayed: boolean
): FailureClassification => {
  switch (reason) {
    case SessionEndReason.USER_ENDED:
    case SessionEndReason.SWITCH_CHANNEL_REQUESTED:
    case SessionEndReason.END_OF_CONTENT:
    case SessionEndReason.SUPERSEDED:
      return FailureClassification.SUCCESS;
    case SessionEndReason.NETWORK_LOST:
      return hasPlayed
        ? FailureClassification.FAILURE_NETWORK
        : FailureClassification.FAILURE_STARTUP;
    case SessionEndReason.PLAYER_ERROR:
      return hasPlayed
        ? FailureClassification.FAILURE_PLAYBACK
        : FailureClassification.FAILURE_STARTUP;
    case SessionEndReason.POLICY_BLOCK:
      return hasPlayed
        ? FailureClassification.FAILURE_POLICY
        : FailureClassification.FAILURE_STARTUP;
    default:
      return FailureClassification.FAILURE_UNKNOWN;
  }
};

/**
 * Derives the high-level `sts` disposition from `failure_classification`,
 * mirroring spec §7.2: startup/policy cancellations map to CANCEL, playback/
 * network failures map to FAIL, everything else is SUCCESS.
 */
export const mapFailureClassificationToStatus = (
  classification: FailureClassification
): string => {
  switch (classification) {
    case FailureClassification.SUCCESS:
      return "SUCCESS";
    case FailureClassification.FAILURE_STARTUP:
    case FailureClassification.FAILURE_POLICY:
      return "CANCEL";
    case FailureClassification.FAILURE_PLAYBACK:
    case FailureClassification.FAILURE_NETWORK:
    case FailureClassification.FAILURE_UNKNOWN:
    default:
      return "FAIL";
  }
};
